import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Spinner } from './EnvLayout';
import { ui, errText } from './envUi';

interface Category { id: number; slug: string; name: string; color: string | null }
interface Item {
  id: number; category: string; name: string; unit: string;
  co2: number; mj: number; felled: number; tip: string;
}
interface Draft { category: string; name: string; unit: string; co2: string; mj: string; felled: string; tip: string }

/** Swatches offered for a category; built-in categories show their themed colour until one is picked. */
const PALETTE = ['#2563eb', '#db2777', '#0d9488', '#ea580c', '#7c3aed', '#ca8a04', '#dc2626', '#475569'];
const BUILT_IN_HEX: Record<string, string> = { drinks: '#2563eb', pack: '#db2777', paper: '#0d9488', life: '#ea580c' };
const dotColor = (c: Category) => c.color ?? BUILT_IN_HEX[c.slug] ?? '#64748b';

const toDraft = (i: Item): Draft => ({
  category: i.category, name: i.name, unit: i.unit,
  co2: String(i.co2), mj: String(i.mj), felled: String(i.felled), tip: i.tip,
});

/** Categories and objects of the CO₂ calculator. */
export default function AdminCarbon() {
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.environmental.carbon.useQuery();
  const refresh = () => utils.environmental.carbon.invalidate();

  const addItem    = trpc.environmental.addCarbonItem.useMutation({ onSuccess: () => { refresh(); setAdding(false); } });
  const updateItem = trpc.environmental.updateCarbonItem.useMutation({ onSuccess: () => { refresh(); setEditId(null); } });
  const delItem    = trpc.environmental.deleteCarbonItem.useMutation({ onSuccess: refresh });
  const moveItems  = trpc.environmental.reorderCarbonItems.useMutation({ onSuccess: refresh });

  const [adding, setAdding]       = useState(false);
  const [editId, setEditId]       = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  if (isLoading) return <Spinner />;
  if (error || !data) return <p className="text-red-600 dark:text-red-400">{error ? errText(error) : 'Could not load the calculator data.'}</p>;

  const { categories, items } = data as { categories: Category[]; items: Item[] };

  /** Swap with the neighbouring item of the same category (the calculator groups by category). */
  const move = (id: number, dir: -1 | 1) => {
    const all  = items.map(i => i.id);
    const cat  = items.find(i => i.id === id)?.category;
    const same = items.filter(i => i.category === cat).map(i => i.id);
    const k = same.indexOf(id) + dir;
    if (k < 0 || k >= same.length) return;
    const a = all.indexOf(id), b = all.indexOf(same[k]);
    [all[a], all[b]] = [all[b], all[a]];
    moveItems.mutate({ orderedIds: all });
  };

  const parse = (d: Draft) => ({
    category: d.category, name: d.name.trim(), unit: d.unit.trim(), tip: d.tip.trim(),
    co2: Number(d.co2), mj: Number(d.mj), felled: Number(d.felled || '0'),
  });

  const err = addItem.error ?? updateItem.error ?? delItem.error ?? moveItems.error;
  const blank: Draft = { category: categories[0]?.slug ?? '', name: '', unit: '', co2: '', mj: '', felled: '0', tip: '' };

  return (
    <div className="flex flex-col gap-10">
      <CategoriesSection categories={categories} items={items} onChanged={refresh} />

      <section>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-xl">
            <h2 className="font-bold">Objects</h2>
            <p className={ui.muted}>
              What people can pick in the calculator. Values are per single use. The first object in the list is the one
              pre-selected when the calculator opens.
            </p>
          </div>
          {!adding && (
            <button className={ui.btn} disabled={categories.length === 0}
                    onClick={() => { setAdding(true); setEditId(null); }}>+ Add object</button>
          )}
        </div>
        {categories.length === 0 && <p className={`${ui.muted} mb-4`}>Add a category first, then add objects to it.</p>}

        {adding && (
          <div className={`${ui.card} mb-6 p-4`}>
            <h3 className="mb-3 font-bold">New object</h3>
            <ItemForm initial={blank} categories={categories} busy={addItem.isPending} submitLabel="Add"
                      onCancel={() => setAdding(false)} onSubmit={d => addItem.mutate(parse(d))} />
          </div>
        )}

        {categories.map(cat => {
          const group = items.filter(i => i.category === cat.slug);
          return (
            <div key={cat.id} className="mb-6">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: dotColor(cat) }} />
                {cat.name} <span className="font-normal text-slate-400 dark:text-slate-500">({group.length})</span>
              </h3>
              {group.length === 0 && <p className={`${ui.muted} py-2`}>No objects (this category is hidden in the calculator until it has one).</p>}
              <ul className="flex flex-col gap-2">
                {group.map((it, idx) => (
                  <li key={it.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
                    {editId === it.id ? (
                      <ItemForm initial={toDraft(it)} categories={categories} busy={updateItem.isPending} submitLabel="Save"
                                onCancel={() => setEditId(null)} onSubmit={d => updateItem.mutate({ id: it.id, ...parse(d) })} />
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                          <button aria-label="Move up" disabled={idx === 0 || moveItems.isPending} onClick={() => move(it.id, -1)}
                                  className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▲</button>
                          <button aria-label="Move down" disabled={idx === group.length - 1 || moveItems.isPending} onClick={() => move(it.id, 1)}
                                  className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▼</button>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-900 dark:text-slate-100">
                            {it.name} <span className="font-normal text-slate-400 dark:text-slate-500">· {it.unit || 'per use'}</span>
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {it.co2} kg CO₂ · {it.mj} MJ · {it.felled} trees cut{it.tip && ` · “${it.tip}”`}
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                          {confirmId === it.id ? (
                            <>
                              <span className="text-xs text-slate-500 dark:text-slate-400">Remove?</span>
                              <button className={ui.btnDanger} onClick={() => { delItem.mutate({ id: it.id }); setConfirmId(null); }}>Yes</button>
                              <button className={ui.btnGhost} onClick={() => setConfirmId(null)}>No</button>
                            </>
                          ) : (
                            <>
                              <button className={ui.btnGhost} onClick={() => { setEditId(it.id); setAdding(false); }}>Edit</button>
                              <button className={ui.btnDanger} onClick={() => setConfirmId(it.id)}>Remove</button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        {err && <p className="text-sm text-red-600 dark:text-red-400">{errText(err)}</p>}
      </section>
    </div>
  );
}

// ─── Categories ──────────────────────────────────────────────────────────────

function Swatches({ value, onPick }: { value: string | null; onPick: (hex: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Colour">
      {PALETTE.map(hex => (
        <button key={hex} type="button" role="radio" aria-checked={value === hex} aria-label={hex}
                onClick={() => onPick(hex)}
                className={`h-6 w-6 rounded-full border-2 ${value === hex ? 'border-slate-900 dark:border-white' : 'border-transparent'}`}
                style={{ background: hex }} />
      ))}
    </div>
  );
}

function CategoriesSection({ categories, items, onChanged }: {
  categories: Category[]; items: Item[]; onChanged: () => void;
}) {
  const add    = trpc.environmental.addCarbonCategory.useMutation({ onSuccess: () => { onChanged(); setNewName(''); setNewColor(PALETTE[(categories.length + 1) % PALETTE.length]); } });
  const upd    = trpc.environmental.updateCarbonCategory.useMutation({ onSuccess: () => { onChanged(); setEditId(null); } });
  const del    = trpc.environmental.deleteCarbonCategory.useMutation({ onSuccess: onChanged });
  const reorder = trpc.environmental.reorderCarbonCategories.useMutation({ onSuccess: onChanged });

  const [newName, setNewName]   = useState('');
  const [newColor, setNewColor] = useState(PALETTE[4]);
  const [editId, setEditId]     = useState<number | null>(null);
  const [edit, setEdit]         = useState<{ name: string; color: string | null }>({ name: '', color: null });
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const move = (id: number, dir: -1 | 1) => {
    const ids = categories.map(c => c.id);
    const i = ids.indexOf(id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate({ orderedIds: ids });
  };

  const err = add.error ?? upd.error ?? del.error ?? reorder.error;

  return (
    <section>
      <h2 className="font-bold">Categories <span className="font-normal text-slate-400 dark:text-slate-500">({categories.length})</span></h2>
      <p className={`${ui.muted} mb-3`}>
        Groups shown in the calculator (Drinks, Packaging…). Removing a category also removes the objects inside it.
      </p>

      <form className="mb-3 flex flex-wrap items-center gap-3"
            onSubmit={e => { e.preventDefault(); add.mutate({ name: newName, color: newColor }); }}>
        <input className={`${ui.input} !w-auto min-w-[12rem] flex-1`} placeholder="New category name" value={newName}
               onChange={e => setNewName(e.target.value)} />
        <Swatches value={newColor} onPick={setNewColor} />
        <button type="submit" className={ui.btn} disabled={add.isPending || !newName.trim()}>+ Add category</button>
      </form>

      <ul className="flex flex-col gap-2">
        {categories.length === 0 && <li className={`${ui.muted} py-3 text-center`}>No categories yet.</li>}
        {categories.map((c, idx) => {
          const count = items.filter(i => i.category === c.slug).length;
          return (
            <li key={c.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
              {editId === c.id ? (
                <div className="flex flex-wrap items-center gap-3">
                  <input className={`${ui.input} !w-auto min-w-[12rem] flex-1`} value={edit.name}
                         onChange={e => setEdit(v => ({ ...v, name: e.target.value }))} />
                  <Swatches value={edit.color} onPick={hex => setEdit(v => ({ ...v, color: hex }))} />
                  <button className={ui.btn} disabled={upd.isPending || !edit.name.trim()}
                          onClick={() => upd.mutate({ id: c.id, name: edit.name, ...(edit.color ? { color: edit.color } : {}) })}>Save</button>
                  <button className={ui.btnGhost} onClick={() => setEditId(null)}>Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <button aria-label="Move up" disabled={idx === 0 || reorder.isPending} onClick={() => move(c.id, -1)}
                            className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▲</button>
                    <button aria-label="Move down" disabled={idx === categories.length - 1 || reorder.isPending} onClick={() => move(c.id, 1)}
                            className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▼</button>
                  </div>
                  <span className="h-4 w-4 shrink-0 rounded-full" style={{ background: dotColor(c) }} />
                  <p className="min-w-0 flex-1 truncate font-semibold text-slate-900 dark:text-slate-100">
                    {c.name} <span className="font-normal text-slate-400 dark:text-slate-500">· {count} object{count === 1 ? '' : 's'}</span>
                  </p>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    {confirmId === c.id ? (
                      <>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Remove{count > 0 ? ` with its ${count} object${count === 1 ? '' : 's'}` : ''}?
                        </span>
                        <button className={ui.btnDanger} onClick={() => { del.mutate({ id: c.id }); setConfirmId(null); }}>Yes</button>
                        <button className={ui.btnGhost} onClick={() => setConfirmId(null)}>No</button>
                      </>
                    ) : (
                      <>
                        <button className={ui.btnGhost} onClick={() => { setEditId(c.id); setEdit({ name: c.name, color: c.color }); }}>Edit</button>
                        <button className={ui.btnDanger} onClick={() => setConfirmId(c.id)}>Remove</button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {err && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errText(err)}</p>}
    </section>
  );
}

// ─── Object form ─────────────────────────────────────────────────────────────

function ItemForm({ initial, categories, busy, submitLabel, onSubmit, onCancel }: {
  initial: Draft; categories: Category[]; busy: boolean; submitLabel: string;
  onSubmit: (d: Draft) => void; onCancel: () => void;
}) {
  const [d, setD] = useState<Draft>(initial);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD(prev => ({ ...prev, [k]: v }));

  const num = (v: string) => v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0;
  const valid = d.name.trim() !== '' && d.category !== '' && num(d.co2) && num(d.mj) && num(d.felled || '0');

  return (
    <form className="grid gap-3 sm:grid-cols-2"
          onSubmit={e => { e.preventDefault(); if (valid) onSubmit(d); }}>
      <div>
        <label className={ui.label}>Name</label>
        <input className={ui.input} value={d.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Paper cup" />
      </div>
      <div>
        <label className={ui.label}>Category</label>
        <select className={ui.input} value={d.category} onChange={e => set('category', e.target.value)}>
          {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className={ui.label}>Unit (what one use is)</label>
        <input className={ui.input} value={d.unit} onChange={e => set('unit', e.target.value)} placeholder="e.g. cup and lid" />
      </div>
      <div>
        <label className={ui.label}>Tip shown to the user</label>
        <input className={ui.input} value={d.tip} onChange={e => set('tip', e.target.value)} placeholder="e.g. Bring your own mug" />
      </div>
      <div>
        <label className={ui.label}>CO₂ per use (kg)</label>
        <input className={ui.input} inputMode="decimal" value={d.co2} onChange={e => set('co2', e.target.value)} placeholder="0.11" />
      </div>
      <div>
        <label className={ui.label}>Energy per use (MJ)</label>
        <input className={ui.input} inputMode="decimal" value={d.mj} onChange={e => set('mj', e.target.value)} placeholder="1.7" />
      </div>
      <div>
        <label className={ui.label}>Trees cut per use</label>
        <input className={ui.input} inputMode="decimal" value={d.felled} onChange={e => set('felled', e.target.value)} placeholder="0" />
      </div>
      <div className="flex items-end justify-end gap-2">
        <button type="button" className={ui.btnGhost} onClick={onCancel}>Cancel</button>
        <button type="submit" className={ui.btn} disabled={busy || !valid}>{submitLabel}</button>
      </div>
    </form>
  );
}
